import { useState, useEffect } from "react";
import { Phone, Clock, X } from "lucide-react";

interface IncomingCallModalProps {
  isOpen: boolean;
  caller: string;
  queue?: string;
  priority?: "high" | "normal" | "low";
  onView: () => void;
  onViewLater: () => void;
}

export function IncomingCallModal(props: IncomingCallModalProps) {
  const [isOpen, setIsOpen] = useState(props.isOpen);
  const [notification, setNotification] = useState<any>(null);
  const [isAnimating, setIsAnimating] = useState(true);

  useEffect(() => {
    setIsOpen(props.isOpen);
  }, [props.isOpen]);

  // Handle the view later button
  const handleViewLater = () => {
    setIsAnimating(false);
    setTimeout(() => {
      setIsOpen(false);
      setNotification(null);
      if (props.onViewLater) props.onViewLater();
    }, 300);
  };

  // Handle the view button (to take the call)
  const handleView = () => {
    setIsOpen(false);
    setNotification(null);
    if (props.onView) props.onView();
  };

  // If modal is closed, return null
  if (!isOpen) return null;

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center pointer-events-none transition-all d ${
        isAnimating ? "opacity-100" : "opacity-0"
      }`}
    >
      <style>{`
        @keyframes slideInScale {
          0% {
            opacity: 0;
            transform: translateY(-30px) scale(0.92);
          }
          100% {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        @keyframes slideOutScale {
          0% {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
          100% {
            opacity: 0;
            transform: translateY(-30px) scale(0.92);
          }
        }
        @keyframes pulse-glow {
          0%, 100% {
            box-shadow: 0 0 20px rgba(185, 28, 28, 0.3), 0 8px 32px rgba(185, 28, 28, 0.15);
          }
          50% {
            box-shadow: 0 0 40px rgba(185, 28, 28, 0.5), 0 8px 32px rgba(185, 28, 28, 0.25);
          }
        }
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes float-icon {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-4px);
          }
        }
        .modal-card {
          animation: ${isAnimating ? "slideInScale" : "slideOutScale"} 0.5s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .glow-effect {
          animation: pulse-glow 2.5s ease-in-out infinite;
        }
        .content-fade {
          animation: fadeIn 0.6s ease-out 0.1s both;
        }
        .buttons-fade {
          animation: fadeIn 0.6s ease-out 0.25s both;
        }
        .float-icon {
          animation: float-icon 2s ease-in-out infinite;
        }
      `}</style>

      <div className="modal-card pointer-events-auto bg-gradient-to-br from-white to-blue-50 rounded-3xl shadow-2xl w-full max-w-md overflow-hidden glow-effect">
        {/* Decorative top accent */}
        <div
        // className=" p-1 bg-gradient-to-r from-red-600 via-red-600 to-red-700"
        // style={{ backgroundColor: "rgb(185 28 28)" }}
        />

        {/* Header Section */}
        <div className="px-6 pt-6 pb-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3 flex-1">
              <div className="relative float-icon">
                <div
                  className="absolute inset-0 rounded-full blur-lg"
                  style={{ backgroundColor: "rgba(185, 28, 28, 0.3)" }}
                />
                <div
                  className="relative p-3 rounded-full"
                  style={{
                    background:
                      "linear-gradient(to bottom right, rgb(220, 38, 38), rgb(185, 28, 28))",
                  }}
                >
                  <Phone className="w-5 h-5 text-white" />
                </div>
              </div>
              <div>
                {/* <h2 className="text-xl font-bold text-gray-900">
                  Incoming Call
                </h2> */}
                <p className="text-xl text-black font-bold">
                  {props.queue || "Support Queue"}
                </p>
              </div>
            </div>
            {/* <button
              onClick={handleViewLater}
              className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-lg hover:bg-gray-100"
              aria-label="Dismiss"
            >
              <X className="w-5 h-5" />
            </button> */}
          </div>
        </div>

        {/* Content Section */}
        <div className="px-6 py-4 content-fade">
          <div
            className="rounded-3xl p-5 border"
            style={{
              backgroundColor: "rgba(185, 28, 30, 0.05)",
              borderColor: "rgba(185, 28, 28, 0.15)",
            }}
          >
            <p className="text-xl font-bold text-gray-900 mb-4">
              {props.caller}
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="px-6 pb-6 buttons-fade  flex justify-center items-center gap-4 ">
          <button
            onClick={handleView}
            className="w-1/2 px-4 py-6 h-3 text-white font-semibold rounded-xl transition-all duration-300 hover:shadow-lg active:scale-95 transform flex items-center justify-center "
            style={{
              backgroundColor: "rgb(185 28 28)",
              boxShadow: "rgba(185, 28, 28, 0.4) 0px 0px 0px 0px",
            }}
          >
            <Phone className="w-4 h-4" />
            View Now
          </button>
          <button
            onClick={handleViewLater}
            className="w-1/2 h-3 px-4 py-6 bg-gray-300 text-gray-700 font-semibold rounded-xl transition-all duration-300  active:scale-95 transform flex items-center justify-center "
          >
            <Clock className="w-4 h-4" />
            View Later
          </button>
        </div>
      </div>
    </div>
  );
}
